/**
 * useClinicScope — Consistent clinic context for clinic-scoped pages
 *
 * Primary source: RBACContext (backed by localStorage — survives refresh + new tabs)
 * Fallback: sessionStorage clinicUser (for display fields)
 *
 * NEVER used as a render blocker — only as a data source.
 */
import { useMemo } from 'react';
import { useRBAC } from '../context/RBACContext';

export function useClinicScope() {
  const { activeClinicId, allowedClinics, isDoctor, isSuperAdmin, isClinicAdmin } = useRBAC();

  // Legacy fallback: read from sessionStorage if RBAC context hasn't resolved yet
  const legacyClinicId = useMemo(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem('treatx_clinic_user'));
      return stored?.clinicId || null;
    } catch {
      return null;
    }
  }, []);

  // Legacy user info from sessionStorage (for display fields: name, avatar, email)
  const legacyUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('treatx_clinic_user')) || null;
    } catch {
      return null;
    }
  }, []);

  // Derive clinicId: RBAC activeClinicId → RBAC allowedClinics[0] (single-clinic) → sessionStorage fallback
  const clinicId = activeClinicId || (allowedClinics.length === 1 ? allowedClinics[0] : null) || legacyClinicId;
  const userId = legacyUser?.id || legacyUser?.userId || null;
  const userName = legacyUser?.name || 'Staff';
  const userEmail = legacyUser?.email || '';

  return {
    clinicId,
    userId,
    userName,
    userEmail,
    allowedClinics,
    isDoctor,
    isSuperAdmin,
    isClinicAdmin,
  };
}
